import React, { Suspense } from 'react';
import { Webhook } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import type {
  WorkflowTriggerRenderStrategy,
  WorkflowWebhookTriggerViewData,
} from '../types';

const { PersistedWorkflowTriggerKind } = PersistedWorkflow;

const LazyWebhookTriggerBody = React.lazy(() =>
  import('./webhook-trigger-body').then((mod) => ({
    default: mod.WebhookTriggerBody,
  }))
);

function isWebhookTrigger(
  trigger: Parameters<WorkflowTriggerRenderStrategy['matches']>[0]
): trigger is WorkflowWebhookTriggerViewData {
  return trigger.kind === PersistedWorkflowTriggerKind.Webhook;
}

function formatProviderLabel(providerKey: string | undefined): string {
  if (providerKey === undefined || providerKey === '') {
    return 'Custom webhook';
  }
  return providerKey.charAt(0).toUpperCase() + providerKey.slice(1);
}

export const webhookTriggerStrategy: WorkflowTriggerRenderStrategy = {
  triggerKind: PersistedWorkflowTriggerKind.Webhook,
  accent: 'webhook',
  badge: 'Webhook',
  matches: isWebhookTrigger,
  getTitle: (trigger) =>
    isWebhookTrigger(trigger) && trigger.name.trim() !== ''
      ? trigger.name
      : 'Webhook',
  getDescription: (trigger) => {
    if (!isWebhookTrigger(trigger)) {
      return 'HTTP webhook';
    }
    const provider = formatProviderLabel(trigger.providerKey);
    const event =
      trigger.eventKey !== undefined && trigger.eventKey !== 'http.request'
        ? trigger.eventKey
        : null;
    if (event !== null) {
      return `${provider} · ${event}`;
    }
    return provider;
  },
  renderIcon: () => <Webhook className="h-4 w-4" />,
  renderBody: (trigger) => {
    if (!isWebhookTrigger(trigger)) {
      return null;
    }
    return (
      <Suspense fallback={null}>
        <LazyWebhookTriggerBody trigger={trigger} />
      </Suspense>
    );
  },
};
